import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Menu, X } from 'lucide-react';

const Navbar = () => {
  const { language, toggleLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '/', label: t.nav.home },
    { href: '/expertise', label: t.nav.expertise },
    { href: '/vision-sourcing', label: language === 'fr' ? 'Vision Sourcing' : 'Sourcing Vision' },
    { href: '/who-we-are', label: t.nav.whoWeAre },
    { href: '/references', label: t.nav.references },
    { href: '/contact', label: t.nav.contact },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav 
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_sap-transformation/artifacts/a7pt8pw2_image.png"
              alt="Walaby Consulting"
              className="h-16 md:h-20 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-${link.href.replace('/', '') || 'home'}`}
                className={`font-heading text-sm font-medium tracking-wide transition-all duration-200 relative ${
                  isActive(link.href) 
                    ? 'text-slate-900' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {link.label}
                {isActive(link.href) && (
                  <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-[#E5A81D]" />
                )}
              </Link>
            ))}
          </div>

          {/* Language Switcher & CTA */}
          <div className="hidden lg:flex items-center space-x-6">
            <button
              onClick={toggleLanguage}
              data-testid="language-switcher"
              className="font-heading text-sm tracking-wide text-slate-600 hover:text-slate-900 transition-colors"
            >
              <span className={language === 'fr' ? 'font-bold text-slate-900' : ''}>FR</span>
              <span className="mx-1 text-slate-300">|</span>
              <span className={language === 'en' ? 'font-bold text-slate-900' : ''}>EN</span>
            </button>
            <Link
              to="/contact"
              data-testid="nav-cta"
              className="bg-[#0F172A] text-white px-6 py-3 font-heading text-sm font-medium tracking-wide hover:bg-[#1E293B] transition-all duration-300"
            >
              {t.hero.cta}
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-slate-900"
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden py-4 bg-white border-t border-slate-100" data-testid="mobile-menu">
            <div className="flex flex-col space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`font-heading text-sm font-medium tracking-wide px-4 py-2 ${
                    isActive(link.href) 
                      ? 'text-slate-900 border-l-2 border-[#E5A81D]' 
                      : 'text-slate-600'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="px-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                <button
                  onClick={toggleLanguage}
                  className="font-heading text-sm tracking-wide"
                >
                  <span className={language === 'fr' ? 'font-bold text-slate-900' : 'text-slate-600'}>FR</span>
                  <span className="mx-1 text-slate-300">|</span>
                  <span className={language === 'en' ? 'font-bold text-slate-900' : 'text-slate-600'}>EN</span>
                </button>
                <Link
                  to="/contact"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="bg-[#0F172A] text-white px-4 py-2 font-heading text-sm font-medium"
                >
                  {t.hero.cta}
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
